import React from "react";
import {
  Stack,
  Group,
  Text,
  Box,
  SimpleGrid,
  Paper,
  Divider,
  ActionIcon,
  Avatar,
  Button,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconThumbUp,
  IconThumbDown,
  IconEdit,
} from "@tabler/icons-react";
import { router } from "@inertiajs/react";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { VehicleVersion, Review, Notes } from "../../../types";
import { reviewNotes } from "../../../utils/review";
import Stars from "../../../components/Stars";
import { Car } from "lucide-react";

dayjs.locale("fr");

type AdminVehiclesShowProps = {
  vehicle: VehicleVersion;
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text
      size="xs"
      fw={700}
      tt="uppercase"
      style={{ letterSpacing: "0.1em", color: "var(--mantine-color-dimmed)" }}
    >
      {children}
    </Text>
  );
}

function BrandLogo({ vehicle }: { vehicle: VehicleVersion }) {
  if (vehicle.brand?.logo) {
    return (
      <img
        src={vehicle.brand.logo}
        alt={vehicle.brand.title}
        style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 8 }}
      />
    );
  }
  return (
    <Avatar size={44} radius="md" color="brand" variant="light">
      <Car />
    </Avatar>
  );
}

function ReviewCard({ review }: { review: Review }) {
  const hasPoints = review.tops || review.flops;
  return (
    <Paper
      p="lg"
      radius="md"
      style={{
        border: "1px solid var(--mantine-color-gray-2)",
      }}
    >
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Stars value={Math.round(review.note / 2)} max={5} />
          <Text size="xs" c="dimmed">
            {dayjs(review.created_at).format("DD MMMM YYYY")}
          </Text>
        </Group>

        {hasPoints && (
          <>
            <Divider />
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {review.tops && (
                <Stack gap={4}>
                  <Group gap={6}>
                    <IconThumbUp
                      size={12}
                      color="var(--mantine-color-teal-7)"
                      stroke={2}
                    />
                    <SectionLabel>Points positifs</SectionLabel>
                  </Group>
                  <Text size="sm">{review.tops}</Text>
                </Stack>
              )}
              {review.flops && (
                <Stack gap={4}>
                  <Group gap={6}>
                    <IconThumbDown
                      size={12}
                      color="var(--mantine-color-red-7)"
                      stroke={2}
                    />
                    <SectionLabel>Points négatifs</SectionLabel>
                  </Group>
                  <Text size="sm">{review.flops}</Text>
                </Stack>
              )}
            </SimpleGrid>
          </>
        )}

        {review.comment && (
          <>
            <Divider />
            <Text
              size="sm"
              c="dark.3"
              style={{ lineHeight: 1.7, fontStyle: "italic" }}
            >
              {review.comment}
            </Text>
          </>
        )}

        <Divider />
        <SimpleGrid cols={2} spacing={4}>
          {reviewNotes.map((note) => {
            const value = (review as Review & Notes)[note.name as keyof Notes];
            if (!value) return null;
            return (
              <Group
                key={note.name}
                justify="space-between"
                wrap="nowrap"
                style={{
                  padding: "4px 8px",
                  borderRadius: 6,
                  background: "var(--mantine-color-gray-0)",
                }}
              >
                <Text size="xs" c="dimmed">
                  {note.label}
                </Text>
                <Stars value={Math.round(value / 2)} max={5} />
              </Group>
            );
          })}
        </SimpleGrid>
      </Stack>
    </Paper>
  );
}

const AdminVehiclesShow: React.FC<AdminVehiclesShowProps> = ({ vehicle }) => {
  const reviews = vehicle.reviews ?? [];

  return (
    <Stack gap="xl">
      <Group justify="space-between" align="center">
        <Group gap={6} align="center">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={() => router.visit("/admin/vehicles")}
          >
            <IconArrowLeft size={15} />
          </ActionIcon>
          <Text size="sm" c="dimmed">
            Véhicules
          </Text>
          <Text size="sm" c="dimmed">
            /
          </Text>
          <Text size="sm" fw={500}>
            {vehicle.modele}
          </Text>
        </Group>
        {vehicle.vehicle_id && (
          <Button
            variant="default"
            leftSection={<IconEdit size={16} />}
            onClick={() =>
              router.visit(`/admin/vehicles/${vehicle.vehicle_id}/edit`)
            }
          >
            Éditer
          </Button>
        )}
      </Group>

      <Paper
        p="xl"
        radius="lg"
        style={{
          background: "var(--mantine-color-gray-0)",
          border: "1px solid var(--mantine-color-gray-2)",
        }}
      >
        <Group justify="space-between" align="center">
          <Group gap="md">
            <BrandLogo vehicle={vehicle} />
            <Box>
              <Text
                size="xs"
                c="brand.6"
                tt="uppercase"
                fw={600}
                style={{ letterSpacing: "0.14em" }}
              >
                {vehicle.brand?.title ?? vehicle.marque}
              </Text>
              <Text size="xl" fw={700} mt={2} style={{ lineHeight: 1.15 }}>
                {vehicle.modele}
              </Text>
              <Text size="sm" c="dimmed" mt={4}>
                {vehicle.sra_commercial}
              </Text>
            </Box>
          </Group>
          {vehicle.note && (
            <Box ta="center">
              <Text
                size="xs"
                c="dimmed"
                tt="uppercase"
                style={{ letterSpacing: "0.1em" }}
                mb={6}
              >
                Note globale
              </Text>
              <Stars value={Math.round(vehicle.note / 2)} max={5} />
            </Box>
          )}
        </Group>
      </Paper>

      {reviews.length > 0 ? (
        <Stack gap="md">
          <Group gap="xs" align="center">
            <SectionLabel>Avis</SectionLabel>
            <Text size="xs" c="dimmed">
              ({reviews.length})
            </Text>
          </Group>
          <Divider />
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </Stack>
      ) : (
        <Paper p="lg" radius="md" withBorder>
          <Text size="sm" c="dimmed" ta="center" fs="italic">
            Aucun avis pour ce véhicule.
          </Text>
        </Paper>
      )}
    </Stack>
  );
};

export default AdminVehiclesShow;
